"use client";

/**
 * Fancybox-powered image lightbox. The library (~110 KB JS + CSS) is
 * code-split and only fetched the first time a visitor opens a design,
 * so pages that never open the lightbox pay nothing for it.
 */

export type ImageLightboxItem = {
  imageSrc: string;
  imageAlt: string;
  title?: string;
};

export type ImageLightboxHooks = {
  onOpen?: () => void;
  onClose?: () => void;
};

type FancyboxChunks = [typeof import("@fancyapps/ui"), unknown];

let fancyboxChunksPromise: Promise<FancyboxChunks> | null = null;

function loadFancybox(): Promise<FancyboxChunks> {
  fancyboxChunksPromise ??= Promise.all([
    import("@fancyapps/ui"),
    import("@fancyapps/ui/dist/fancybox/fancybox.css"),
  ]);

  return fancyboxChunksPromise;
}

/**
 * Warm the Fancybox chunks (e.g. when the gallery tab becomes active) so
 * the lightbox opens instantly when a design is clicked.
 */
export function preloadImageLightbox(): void {
  if (typeof window === "undefined") return;

  void loadFancybox().catch(() => {
    // Allow a later interaction to retry the fetch.
    fancyboxChunksPromise = null;
  });
}

export async function openImageLightbox(
  items: ImageLightboxItem[],
  startIndex = 0,
  hooks?: ImageLightboxHooks,
): Promise<void> {
  if (typeof window === "undefined" || items.length === 0) return;

  const [{ Fancybox }] = await loadFancybox();

  const instance = Fancybox.show(
    items.map((item) => ({
      type: "image",
      src: item.imageSrc,
      thumb: item.imageSrc,
      alt: item.imageAlt,
      caption: item.title ?? item.imageAlt,
    })),
    {
      startIndex,
      closeExisting: true,
      mainClass: "ubp-image-lightbox",
      Carousel: {
        Toolbar: {
          display: {
            left: ["counter"],
            middle: ["zoomIn", "zoomOut", "toggle1to1", "reset"],
            right: ["close"],
          },
        },
      },
      on: {
        ready: () => hooks?.onOpen?.(),
        destroy: () => hooks?.onClose?.(),
      },
    },
  );

  // Fancybox refused to open — release hooks so callers can clean up.
  if (!instance) {
    hooks?.onClose?.();
  }
}
